/**
 * CollisionDisambiguation — post-pair prompt shown between "paired!" and the
 * home view when the auto-name pipeline produced a name that already exists
 * in the user's pair set (browser mirror of Android's
 * `CollisionDisambiguationStep.kt`).
 *
 * Three context-prefixed chip suggestions ("Work / Home / Personal <name>"),
 * a free-text custom field that overrides any chip selection, and a tertiary
 * "use the original anyway" link for users who want the duplicate.
 *
 * Headless: stateless w.r.t. storage. The host owns the rename API call and
 * the storage clear; this only collects the chosen name.
 *
 * Extracted from two independent copies (`apps/extension` and `apps/web`)
 * whose logic was identical — they differed only in i18n access and styling.
 */
import { MAX_DEVICE_NAME_LENGTH } from "@reflecto/client-core";
import { useState } from "react";
import { Button } from "../Button";
import styles from "./CollisionDisambiguation.module.css";

/**
 * Suggestion chip — module-scope to keep the function reference stable across
 * renders. Defining it inside `CollisionDisambiguation` would create a new
 * component identity on every keystroke, causing React to unmount/remount each
 * chip and lose any keyboard focus.
 */
function Chip({
  name,
  selected,
  saving,
  onSelect,
}: {
  name: string;
  selected: boolean;
  saving: boolean;
  onSelect: (name: string) => void;
}) {
  return (
    <button
      type="button"
      className={styles.chip}
      data-selected={selected ? "true" : "false"}
      aria-pressed={selected}
      onClick={() => onSelect(name)}
      disabled={saving}
    >
      {name}
    </button>
  );
}

export interface CollisionDisambiguationLabels {
  /** aria-label for the step's <section>. */
  ariaLabel: string;
  /** Heading, given the colliding name. */
  title: (name: string) => string;
  subtitle: string;
  /** The three chip suggestions, each given the colliding name to prefix. */
  chipWork: (name: string) => string;
  chipHome: (name: string) => string;
  chipPersonal: (name: string) => string;
  customLabel: string;
  save: string;
  saveFailed: string;
  useAnyway: string;
}

export interface CollisionDisambiguationProps {
  /** The auto-generated name that collided with an existing peer. */
  originalName: string;
  /** Invoked with the user's chosen final name (chip or custom). */
  onSave: (name: string) => Promise<void> | void;
  /** Invoked when the user opts to keep the duplicate name. */
  onUseAnyway: () => Promise<void> | void;
  labels: CollisionDisambiguationLabels;
}

export function CollisionDisambiguation({
  originalName,
  onSave,
  onUseAnyway,
  labels,
}: CollisionDisambiguationProps) {
  const work = labels.chipWork(originalName);
  const home = labels.chipHome(originalName);
  const personal = labels.chipPersonal(originalName);
  const [selected, setSelected] = useState<string | null>(null);
  const [custom, setCustom] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  // Resolution order matches Android's collision step:
  //   custom (trimmed, non-empty) > selected chip > nothing
  // When neither is set, `finalName` is null and Save is disabled — falling back
  // to `originalName` would let the user "rename" to the colliding name they
  // already have, silently the same outcome as "use the original anyway".
  const finalName = (() => {
    const trimmed = custom.trim();
    if (trimmed) return trimmed;
    return selected;
  })();
  const canSave = finalName != null && !saving;

  const handleSave = async () => {
    if (finalName == null) return; // belt-and-suspenders; Save is disabled in this state.
    setSaving(true);
    setError(false);
    try {
      await onSave(finalName);
    } catch {
      setError(true);
    } finally {
      setSaving(false);
    }
  };

  const handleChipSelect = (name: string) => {
    setSelected(name);
    setCustom("");
  };

  return (
    <section
      className={styles.root}
      aria-label={labels.ariaLabel}
      data-testid="collision-disambiguation"
    >
      <h2 className={styles.title}>{labels.title(originalName)}</h2>
      <p className={styles.subtitle}>{labels.subtitle}</p>
      <div className={styles.chips}>
        <Chip
          name={work}
          selected={selected === work}
          saving={saving}
          onSelect={handleChipSelect}
        />
        <Chip
          name={home}
          selected={selected === home}
          saving={saving}
          onSelect={handleChipSelect}
        />
        <Chip
          name={personal}
          selected={selected === personal}
          saving={saving}
          onSelect={handleChipSelect}
        />
      </div>
      <label className={styles.customLabel}>
        <span>{labels.customLabel}</span>
        <input
          type="text"
          className={styles.customInput}
          value={custom}
          maxLength={MAX_DEVICE_NAME_LENGTH}
          onChange={(e) => {
            setCustom(e.target.value);
            setSelected(null);
          }}
          disabled={saving}
        />
      </label>
      <Button
        variant="primary"
        size="m"
        fullWidth
        loading={saving}
        onClick={handleSave}
        disabled={!canSave}
      >
        {labels.save}
      </Button>
      {error && (
        <span className={styles.errorMessage} role="alert">
          {labels.saveFailed}
        </span>
      )}
      <button
        type="button"
        className={styles.useAnywayLink}
        onClick={onUseAnyway}
        disabled={saving}
      >
        {labels.useAnyway}
      </button>
    </section>
  );
}
